import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Chip,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UserAuthentication from '../../../../components/userAuthentication/index';
import TreatmentsModal from '../treatmentModal';
import ProcedureListRouter from '../procedures/procedureList/procedureListRouter';
import ApproveTreatmentModal from '../treatmentApproveModal';
import RefuseTreatmentModal from '../treatmentRefuseModal';
import FinishTreatmentModal from '../treatmentFinishModal';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../../../../components/loading';
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import IncomesModal from '../../../../financial/incomes/incomesModal';
import { useClinic } from '../../../../hooks/useClinic';
import { useTreatments } from '../hooks/useTreatments';
import { useTreatmentActions } from '../hooks/useTreatmentActions';
import HowItWorksLink from '../../../../components/howItWorks/howItWorksLink';
import HowItWorksEmptyState from '../../../../components/howItWorks/howItWorksEmptyState';
import {
  TREATMENT_FLOW_FEATURE_KEY,
  TREATMENT_FLOW_SUBTITLE,
  TREATMENT_FLOW_TITLE,
  treatmentFlowStages,
} from '../treatmentFlow';
import { isApproved, isFinished, isRefused, statusChipColor } from '../treatmentStatus';

export default function TreatmentsList() {
  const { patientId } = useParams();

  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [treatments, setTreatments] = useState([]);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [treatmentForActions, setTreatmentForActions] = useState(null);
  const [isNewTreatmentModalOpen, setIsNewTreatmentModalOpen] = useState(false);
  const [anchorEl, setActionsMenuAnchorEl] = useState(null);
  const [showProcedureComponent, setShowProcedureComponent] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);

  const [openIncomeModal, setOpenIncomeModal] = useState(false);
  const [incomeDescription, setIncomeDescription] = useState('');
  const [incomeAmount, setIncomeAmount] = useState(0);
  const [incomeProfessionalId, setIncomeProfessionalId] = useState(null);

  const { clinic } = useClinic();
  const usesInsurance = clinic?.usesInsurance ?? false;

  const {
    treatments: fetchedTreatments,
    isLoading: isTreatmentsLoading,
    refetch: getTreatmentsFromPatient,
  } = useTreatments(patientId);
  const {
    approve,
    refuse,
    finish,
    remove,
    generateCharge: generateChargeRequest,
  } = useTreatmentActions(patientId);

  useEffect(() => {
    setTreatments(fetchedTreatments);
  }, [fetchedTreatments]);

  const openTreatmentModal = () => setIsNewTreatmentModalOpen(true);
  const closeTreatmentModal = () => setIsNewTreatmentModalOpen(false);

  const setTreatmentClicked = (event, treatment) => {
    setActionsMenuAnchorEl(event.currentTarget);
    setTreatmentForActions(treatment);
    setSelectedTreatment(treatment);
  };

  const closeTreatmentActionsMenu = () => setActionsMenuAnchorEl(null);

  const onTreatmentSaved = (returnFromApi) => {
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
    getTreatmentsFromPatient();
    closeTreatmentModal();
  };

  const onProcedureSaved = () => {
    getTreatmentsFromPatient();
  };

  const openProcedures = () => {
    setShowProcedureComponent(true);
    closeTreatmentActionsMenu();
  };

  const openApproveModal = () => {
    setIsApproveModalOpen(true);
    closeTreatmentActionsMenu();
  };

  const confirmTreatmentApproval = () => {
    setIsApproveModalOpen(false);

    setLoading(true);
    approve(selectedTreatment.treatmentId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        setLoading(false);
        getTreatmentsFromPatient();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  };

  const openRefuseModal = () => {
    setIsRefuseModalOpen(true);
    closeTreatmentActionsMenu();
  };

  const confirmTreatmentRefusal = (refusalReason) => {
    setIsRefuseModalOpen(false);

    setLoading(true);
    refuse(selectedTreatment.treatmentId, refusalReason)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        setLoading(false);
        getTreatmentsFromPatient();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  };

  const openFinishModal = () => {
    setIsFinishModalOpen(true);
    closeTreatmentActionsMenu();
  };

  const confirmTreatmentConclusion = (finishDate) => {
    setIsFinishModalOpen(false);

    setLoading(true);
    finish(selectedTreatment.treatmentId, finishDate)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        setLoading(false);
        getTreatmentsFromPatient();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  };

  const deleteSelectedTreatment = () => {
    closeTreatmentActionsMenu();

    setLoading(true);
    remove(selectedTreatment.treatmentId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        setLoading(false);
        setShowProcedureComponent(false);
        getTreatmentsFromPatient();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  };

  const printTreatment = () => {
    window.open(`/treatment-to-print/${selectedTreatment.treatmentId}`, '_blank');
  };

  const printEvolutionSheet = () => {
    closeTreatmentActionsMenu();
    window.open(`/treatment-evolution-to-print/${selectedTreatment.treatmentId}`, '_blank');
  };

  const generateCharge = () => {
    closeTreatmentActionsMenu();

    setLoading(true);
    generateChargeRequest(selectedTreatment.treatmentId)
      .then((returnFromApi) => {
        const charge = returnFromApi.data;
        setIncomeAmount(charge.amount);
        setIncomeDescription(charge.description);
        setIncomeProfessionalId(charge.professionalId);
        setLoading(false);
        setOpenIncomeModal(true);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      });
  };

  const saveIncome = (returnFromApi) => {
    setShowSuccessMessage(true);
    setSuccessMessage(returnFromApi.data);
    setOpenIncomeModal(false);
  };

  const treatmentClick = (treatment) => {
    setSelectedTreatment(treatment);
    setShowProcedureComponent(true);
  };

  return (
    <>
      <Loading isToHidden={isLoading === false && isTreatmentsLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
        <Box sx={{ flex: '1.5', minWidth: 0 }}>
          <UserAuthentication />
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardHeader
              title={
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Tratamentos
                </Typography>
              }
              subheader={
                <HowItWorksLink
                  featureKey={TREATMENT_FLOW_FEATURE_KEY}
                  title={TREATMENT_FLOW_TITLE}
                  subtitle={TREATMENT_FLOW_SUBTITLE}
                  stages={treatmentFlowStages}
                />
              }
              action={
                <Button
                  data-cy="add-treatment-btn"
                  variant="outlined"
                  disableElevation
                  onClick={openTreatmentModal}
                >
                  Adicionar Tratamento
                </Button>
              }
            />
            <CardContent>
              {!isLoading && treatments.length === 0 ? (
                <HowItWorksEmptyState
                  title="Nenhum tratamento cadastrado ainda"
                  subtitle={TREATMENT_FLOW_SUBTITLE}
                  stages={treatmentFlowStages}
                  actionLabel="Adicionar Tratamento"
                  onAction={openTreatmentModal}
                />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {usesInsurance && (
                          <TableCell sx={{ fontWeight: 'bold' }}>Convênio</TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 'bold' }}>Profissional</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Tratamento</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Valor Total</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {treatments.map((treatment) => (
                        <TableRow
                          data-cy="treatment-row"
                          key={treatment.treatmentId}
                          hover
                          onClick={() => treatmentClick(treatment)}
                          selected={selectedTreatment?.treatmentId === treatment.treatmentId}
                          sx={{ cursor: 'pointer' }}
                        >
                          {usesInsurance && <TableCell>{treatment.covenant}</TableCell>}
                          <TableCell>{treatment.dentistName}</TableCell>
                          <TableCell>{treatment.description}</TableCell>
                          <TableCell>
                            {(treatment.formattedTotalAmount || 0).toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            })}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={treatment.status}
                              color={statusChipColor(treatment.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                setTreatmentClicked(event, treatment);
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Menu anchorEl={anchorEl} onClose={closeTreatmentActionsMenu} open={Boolean(anchorEl)}>
            <MenuItem data-cy="see-procedure-btn" onClick={openProcedures}>
              Ver Procedimentos
            </MenuItem>
            <MenuItem onClick={printTreatment}>
              {isApproved(treatmentForActions) ? 'Imprimir Tratamento' : 'Imprimir Orçamento'}
            </MenuItem>
            <Tooltip
              placement="left"
              title={isApproved(treatmentForActions) ? 'Este tratamento já foi aprovado.' : ''}
            >
              <span>
                <MenuItem onClick={openApproveModal} disabled={isApproved(treatmentForActions)}>
                  Aprovar Tratamento
                </MenuItem>
              </span>
            </Tooltip>
            <Tooltip
              placement="left"
              title={
                isApproved(treatmentForActions)
                  ? 'Este tratamento já foi aprovado.'
                  : isRefused(treatmentForActions)
                    ? 'Este orçamento já está marcado como não aprovado.'
                    : ''
              }
            >
              <span>
                <MenuItem
                  data-cy="treatment-refuse-btn"
                  onClick={openRefuseModal}
                  disabled={isApproved(treatmentForActions) || isRefused(treatmentForActions)}
                >
                  Marcar como Não Aprovado
                </MenuItem>
              </span>
            </Tooltip>
            <MenuItem onClick={printEvolutionSheet}>Imprimir Ficha de Evolução Clínica</MenuItem>
            <Tooltip
              placement="left"
              title={isFinished(treatmentForActions) ? 'Este tratamento já foi concluído.' : ''}
            >
              <span>
                <MenuItem
                  data-cy="treatment-finish-btn"
                  onClick={openFinishModal}
                  disabled={isFinished(treatmentForActions)}
                >
                  Concluir Tratamento
                </MenuItem>
              </span>
            </Tooltip>
            <MenuItem onClick={generateCharge}>Gerar Cobrança</MenuItem>
            <Tooltip
              placement="left"
              title={
                isApproved(treatmentForActions)
                  ? 'Não é possível deletar um tratamento já aprovado.'
                  : ''
              }
            >
              <span>
                <MenuItem
                  data-cy="treatment-delete-btn"
                  onClick={deleteSelectedTreatment}
                  sx={{ color: 'error.main' }}
                  disabled={isApproved(treatmentForActions)}
                >
                  Deletar
                </MenuItem>
              </span>
            </Tooltip>
          </Menu>

          <TreatmentsModal
            open={isNewTreatmentModalOpen}
            onCloseTreatmentModal={closeTreatmentModal}
            onTreatmentSaved={onTreatmentSaved}
            usesInsurance={usesInsurance}
          />
        </Box>

        {showProcedureComponent && (
          <Box sx={{ flex: '1.3', minWidth: 0 }}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <ProcedureListRouter
                  treatment={selectedTreatment}
                  onProcedureSaved={onProcedureSaved}
                  patientId={patientId}
                />
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
      <ApproveTreatmentModal
        open={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        onConfirm={confirmTreatmentApproval}
        treatmentDescription={treatmentForActions?.descricao || ''}
      />

      <RefuseTreatmentModal
        open={isRefuseModalOpen}
        onClose={() => setIsRefuseModalOpen(false)}
        onConfirm={confirmTreatmentRefusal}
        treatmentDescription={treatmentForActions?.description || ''}
      />

      <FinishTreatmentModal
        open={isFinishModalOpen}
        onClose={() => setIsFinishModalOpen(false)}
        onConfirm={confirmTreatmentConclusion}
        treatmentDescription={treatmentForActions?.description || ''}
      />

      {openIncomeModal && (
        <IncomesModal
          open
          onClose={() => setOpenIncomeModal(false)}
          onSaveSuccess={saveIncome}
          incomeId={null}
          incomeDescription={incomeDescription}
          incomeAmount={incomeAmount}
          incomeOrigin={'TREATMENT'}
          patientId={patientId}
          professionalId={incomeProfessionalId}
        />
      )}
    </>
  );
}
